import { getWarningMetaFromSchema } from '@framework/validation';
import { Form, FormItemProps } from 'antd';
import clx from 'classnames';
import * as React from 'react';
import { useEffect, useId, useMemo } from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';
import styled from 'styled-components';
import { useField } from '../field/useField';
import { useFieldSchemaValidation } from '../field/useFieldSchemaValidation';
import { LeafError } from '../form/errors/LeafError';
import { useFormSectionContext } from '../form/section/FormSectionContext';
import { FormItemContext } from './FormItemContext';
import { getAllFormItemErrors } from './getAllFormItemErrors';
import { FormItemAccessibilityLabel } from './label/FormItemAccessibilityLabel';
import { isBooleanCheckboxElement } from './symbols/isBooleanCheckboxElement';
import { isRadioOrCheckboxGroupElement } from './symbols/isRadioOrCheckboxGroupElement';

export interface FormItemLayout {
  removeMargin?: boolean;
  removeLabelSpace?: boolean;
  removeHelpMargin?: boolean;
  hideVisualLabel?: boolean;
}

export const StyledFormItem = styled(Form.Item)`
  &.remove-label-space {
    .form-handler & {
      padding-top: 0;
    }
    // per input tabella (spostato in TableInputWidgetRowsTable)
    .ant-radio-wrapper,
    .ant-checkbox-wrapper {
      .form-handler & {
        /* margin-top: 0; */
      }
    }
  }
  &.remove-help-margin {
    margin-bottom: 0;
  }
  /* Per l'accessibilità è necessario che il label sia presente nell'html */
  &.hide-visual-label {
    .ant-form-item-label label {
      .form-handler & {
        position: absolute;
        max-width: 1000px;
        left: -9999px;
        overflow-x: hidden;
      }
    }
  }
  // per rimuovere l'asterisco ross nei campi booleani richiesti
  &.remove-requires-asterisk {
    .ant-form-item-label > label.ant-form-item-required::before {
      display: none;
    }
  }
  // per allineare i messaggi di errore e warning
  &.align-flex-end {
    .ant-form-item-explain-error {
      text-align: right;
    }
  }
  &.align-center {
    .ant-form-item-explain-error {
      text-align: center;
    }
  }
`;

function getValidateStatus(params: {
  isTouched: boolean;
  errors: LeafError[];
  warnings: string[];
  isSubmitted: boolean | undefined;
}) {
  const { isTouched, errors, warnings, isSubmitted } = params;
  // Se non è stato toccato e non è stato inviato, non mostriamo alcun feedback
  // In questo modo non mostriamo gli errori e i warning ai primi render, ma solo dopo un tentativo di invio
  if (!isTouched && !isSubmitted) return 'success' as const;
  if (errors.length > 0) return 'error' as const;
  if (warnings.length > 0) return 'warning' as const;
  return 'success' as const;
}

function getErrorsText(params: {
  validateStatus: 'error' | 'warning' | 'success';
  errors: LeafError[];
  warnings: string[];
}) {
  const { validateStatus, errors, warnings } = params;
  const hasError = validateStatus === 'error';
  const hasWarning = validateStatus === 'warning';

  return hasError
    ? errors.map(e => e.error.message).join(', ')
    : hasWarning
    ? warnings.join(', ')
    : undefined;
}

export interface IFormItemProps<T extends FieldValues = Record<string, any>>
  // Layout è stato introdotto anche da `antd`, ma noi usiamo già la prop internamente
  extends Omit<FormItemProps, 'layout'> {
  /** Se fornito un array, il form gestirà più campi contemporaneamente */
  name: FieldPath<T> | Array<FieldPath<T>> | undefined;
  children: React.ReactNode;
  layout?: FormItemLayout;
  /** Se inidcato, mostra soltanto gli errori di "primo livello", relativi specificatamente al campo e non ai suoi figli (e.g. liste ripetibili) */
  onlyFirstLevelErrors?: boolean;

  /** Se impostato a true, il campo del formItem è in sola lettura, e di conseguenza disabilito le configurazioni grafiche (feedback, status, messages) */
  readonly?: boolean;
  /**
   * Solo se inserito in un `RowElement`, permette di specificare il numero
   * di colonne
   */
  colSpan?: number;
}

export function FormItem<T extends FieldValues = Record<string, any>>(
  props: IFormItemProps<T>,
) {
  const {
    name,
    children,
    className,
    help,
    onlyFirstLevelErrors,
    readonly,
    layout,
    ...itemProps
  } = props;

  const {
    error,
    visible,
    enabled,
    layoutEnabled,
    touched,
    schema,
    unregister,
    getActualSchema,
    isSubmitted,
    getValues,
  } = useField(name);

  const isTouched = touched.some(t => t);

  const errors = useMemo(() => {
    return getAllFormItemErrors(error).filter(e =>
      onlyFirstLevelErrors ? e.path.length === 0 : true,
    );
  }, [error, onlyFirstLevelErrors]);

  const warnings = useMemo(() => {
    if (!schema) return [];

    const result = schema
      ?.map(s => getWarningMetaFromSchema(s))
      .filter((w: string | null): w is string => w !== null);

    return result;
  }, [schema]);

  // Ottiene l'id per l'attributo aria-labelledby
  const ariaLabelId = useId();
  // Meta-informazioni di validazione dello schema (required, spec)
  const validation = useFieldSchemaValidation(schema);

  /**
   * Al cambio di visibilità, se il campo viene nascosto allora de-registriamo il campo
   * per evitare che il form mantenga il valore del campo nascosto.
   */
  useEffect(() => {
    if (!visible) {
      unregister();
      return;
    }

    // All'unmount, se lo schema prevede lo strip, de-registriamo il campo.
    // Serve ri-calcolarlo perché i valori potrebbero essere cambiati
    return () => {
      const actual = getActualSchema();
      if (actual?.spec?.strip) unregister();
    };
  }, [getActualSchema, unregister, visible]);

  const [labelAddition, setLabelAddition] = React.useState<
    React.ReactNode | undefined
  >();

  const context = useMemo(
    () => ({
      ariaLabelId,
      errors,
      validation,
      enabled,
      schema,
      warnings,
      layoutEnabled,
      readonly,
      name: Array.isArray(name) ? name : [name],
      setLabelAddition,
    }),
    [
      ariaLabelId,
      errors,
      warnings,
      validation,
      enabled,
      schema,
      layoutEnabled,
      readonly,
      name,
    ],
  );

  // Utilizzato per lo scroll-to CSS in caso di errore. vedere ErrorList
  const dataFormName = (Array.isArray(name) ? name : [name]).join(' ');

  const { validateStatus, errorsText, hasFeedback } = useMemo(() => {
    // Se il campo è in sola lettura, non mostriamo alcun feedback (manteniamo un layout "di default")
    if (readonly) {
      return {
        validateStatus: '' as const,
        errorsText: undefined,
        hasFeedback: false,
      };
    }
    const validateStatus = getValidateStatus({
      errors,
      isTouched,
      warnings,
      isSubmitted,
    });
    const errorsText = getErrorsText({ validateStatus, errors, warnings });

    const allValues = Object.values(getValues() ?? {});

    /**
     * Impostiamo il feedback (iconcina) ai campi solo se sono Touched o si è inviato il form.
     * Questo avviene perché se tenessimo il il "touched", i campi che _non_ sono stati "toccati"
     * durante la compilazione non avrebbero l'iconcina di errore.
     *
     * Inoltre, in base alla `mode` del FormHandler (onBlur, onChange, onSubmit), il "touched" potrebbe
     * non essere impostato: per esempio in caso di `onChange` viene impostato solo _alla modifica_ del campo
     * mentre in `onBlur` il touched viene impostato a true anche con un focus-unfocus del campo.
     *
     * Notare che il salvataggio in bozza _non_ imposta il "isSubmitted" (dato che utilizza un flusso di
     * submit custom che non passa direttamente per il formHandler).
     */
    const hasFeedback =
      itemProps.hasFeedback !== false &&
      (isTouched || isSubmitted) &&
      // Verifichiamo che tutti i valori siano definiti, altrimenti non mostriamo
      // il feedback
      (allValues.every((v: any) => v != null) || validateStatus !== 'success');

    return { validateStatus, errorsText, hasFeedback };
  }, [
    errors,
    isSubmitted,
    isTouched,
    itemProps.hasFeedback,
    readonly,
    warnings,
    getValues,
  ]);

  // Visualizzazione
  const sectionContext = useFormSectionContext();

  if (!visible) return null;

  const isRadioOrCheckboxGroup = React.Children.toArray(children).some(
    isRadioOrCheckboxGroupElement,
  );

  const isBooleanCheckbox = React.Children.toArray(children).some(
    isBooleanCheckboxElement,
  );

  return (
    <FormItemContext.Provider value={context}>
      <StyledFormItem
        hasFeedback={hasFeedback}
        validateStatus={validateStatus}
        extra={help}
        help={errorsText}
        required={validation?.required}
        data-form-name={dataFormName}
        {...itemProps}
        className={clx(className, {
          'remove-label-space':
            layout?.removeLabelSpace ?? layout?.removeMargin,
          'remove-help-margin':
            layout?.removeHelpMargin ??
            layout?.removeMargin ??
            sectionContext?.removeHelpMargin,
          'hide-visual-label': layout?.hideVisualLabel,
          'remove-requires-asterisk': isBooleanCheckbox || readonly,
        })}
        htmlFor={isRadioOrCheckboxGroup ? undefined : ariaLabelId}
        label={
          !isRadioOrCheckboxGroup ? (
            props.label && (
              <>
                {props.label}
                {labelAddition}
              </>
            )
          ) : (
            <FormItemAccessibilityLabel id={ariaLabelId}>
              {props.label}
              {labelAddition}
            </FormItemAccessibilityLabel>
          )
        }
      >
        {props.children}
      </StyledFormItem>
    </FormItemContext.Provider>
  );
}
